<template>
  <div>
    <h1> {{ $t('signupAuthentication.title') }} </h1>
    <form @submit.prevent="handleSubmit">
      <FormInput
        id="register-email"
        v-model="form.email"
        :disabled="hasForcedEmail"
        :label="$t('signupAuthentication.label-mail')"
        required
        type="email"
        @blur="$v.form.email.$touch()"
      >
        <template v-if="$v.form.email.$dirty" #error>
          <template v-if="emailAlreadyUsed"> {{ $t('signupAuthentication.mail-used') }} </template>
          <template v-else-if="!$v.form.email.required"> {{ $t('signupAuthentication.mail-required') }} </template>
          <template v-else-if="$v.form.email.$invalid"> {{ $t('signupAuthentication.mail-invalid') }} </template>
        </template>
      </FormInput>

      <FormInput
        id="register-password"
        v-model="form.password"
        :show-password="showPassword"
        :label="$t('signupAuthentication.label-password')"
        required
        type="password"
        @blur="$v.form.password.$touch()"
        @toggle-password-visibility="showPassword = !showPassword"
      >
        <template #help> {{ $t('signupAuthentication.characters-minimum-required') }} </template>
        <template v-if="$v.form.password.$dirty" #error>
          <template v-if="!$v.form.password.required"> {{ $t('signupAuthentication.password-required') }} </template>
          <template v-else-if="!$v.form.password.minLength"> {{ $t('signupAuthentication.characters-minimum') }} </template>
        </template>
      </FormInput>

      <DashedLine />

      <FormCheckbox v-model="form.acceptNotice" class="accept-notice">
        <template #default>
          {{$t('signupAuthentication.notice.1')}}
          <a :href="$setting('NOTICE_LINK')" target='_blank'> {{$t('signupAuthentication.notice.link')}} </a>
          {{$t('signupAuthentication.notice.2')}}
        </template>
        <template v-if="$v.form.acceptNotice.$dirty" #error>
          <template v-if="$v.form.acceptNotice.$invalid"> {{ $t('signupAuthentication.notice-invalid') }} </template>
        </template>
      </FormCheckbox>

      <FormCheckbox v-model="form.acceptTerms" class="accept-terms">
        <template #default>
          {{ $t('signupAuthentication.condition.1') }}
          <a :href="$setting('PRIVACY_LINK')" target='_blank'> {{ $t('signupAuthentication.condition.link-1') }} </a>
          {{ $t('signupAuthentication.condition.2') }}
          <a :href="$setting('TERMS_LINK')" target='_blank'> {{ $t('signupAuthentication.condition.link-2') }} </a>
        </template>
        <template v-if="$v.form.acceptTerms.$dirty" #error>
          <template v-if="$v.form.acceptTerms.$invalid"> {{ $t('signupAuthentication.accept-conditions') }} </template>
        </template>
      </FormCheckbox>

      <SignupButtons :can-be-submitted="!$v.$invalid" :is-submitting="isSubmitting"/>
    </form>
  </div>
</template>

<script>
  import FormInput from '@/components/Form/FormInput';
  import FormCheckbox from '@/components/Form/FormCheckbox';
  import DashedLine from '@/components/DashedLine';
  import Auth from '@/api/auth.js';
  import SignupButtons from '@/components/Signup/SignupButtons';
  import { email, minLength, required } from '@byscripts/vuelidate/lib/validators';

  export default {
    name: 'SignupAuthentication',
    components: {
      SignupButtons,
      DashedLine,
      FormCheckbox,
      FormInput,
    },
    data() {
      return {
        form: {
          email: this.$store.state.auth.forcedEmail || this.$store.state.signup.form.email,
          password: this.$store.state.signup.form.password,
          acceptTerms: this.$store.state.signup.form.acceptTerms || false,
          acceptNotice: this.$store.state.signup.form.acceptNotice || false,
        },
        isSubmitting: false,
        showPassword: true,
        emailAlreadyUsed: false,
      };
    },
    validations: {
      form: {
        email: {
          required,
          email,
        },
        password: {
          required,
          minLength: minLength(8),
        },
        acceptTerms: {
          required,
          true: (v) => v === true,
        },
        acceptNotice: {
          required,
          true: (v) => v === true,
        },
      },
    },
    computed: {
      hasForcedEmail() {
        return !!this.$store.state.auth.forcedEmail;
      },
    },
    methods: {
      async handleSubmit() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          return this.$addError(this.$t('signupAuthentication.check-form'));
        }

        try {
          this.isSubmitting = true;
          this.emailAlreadyUsed = false;
          this.showPassword = false;

          await Auth.checkIfEmailExists(this.form.email);

          await this.$store.dispatch('signup/saveForm', { form: this.form });
          await this.$store.dispatch('auth/setCredentials', { email: this.form.email, password: this.form.password });
          await this.$store.dispatch('signup/goToNextStep');
        } catch (e) {
          if (e.response?.status === 400) {
            this.emailAlreadyUsed = true;
          } else {
            throw e;
          }
        } finally {
          this.isSubmitting = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .form-input {
    margin-bottom: 3rem;
  }

  .dashed-line {
    margin: 4rem 0 3rem;
  }

  .accept-terms, .accept-notice {
    font-size: 1.4rem;
    margin-bottom: 2rem;
    color: $fadedTextColor;

    a {
      color: $fadedTextColor
    }
  }

  ::v-deep .signup-buttons.has-back-button{
      @include onMobile{
        column-gap: 3.7rem;
        justify-content: end;
        flex-wrap: wrap;
      }
  }
</style>
